import { useMemo } from "react";
import { motion } from "framer-motion";
import {
  MousePointerClick,
  AlertTriangle,
  Sparkles,
  ArrowRight,
} from "lucide-react";
import { getPlaybookForIndustry } from "./conversion-playbooks";

/**
 * ConversionChecklist — industry-aware funnel walkthrough that replaces
 * the former AI landing-page analysis.
 *
 * Static content, matched to the client's industry. Designed to feel like
 * a thoughtful editorial guide, not an audit tool.
 */
export const ConversionChecklist = ({ clientName = "Your business", industry = "" }) => {
  const playbook = useMemo(() => getPlaybookForIndustry(industry), [industry]);
  const { scenarioLabel, label, steps, biggestLeak, quickWin } = playbook;

  return (
    <div className="relative" data-testid="conversion-checklist">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-3">
          <MousePointerClick className="w-4 h-4 text-lime-300" />
          <p className="text-lime-300 text-[11px] md:text-xs font-bold uppercase tracking-[0.3em]">
            The Conversion Checklist
          </p>
        </div>
        <h3 className="text-2xl md:text-4xl lg:text-5xl font-bold text-white leading-[1.1] mb-3 max-w-4xl">
          They're going to {clientName}'s website.
          <br />
          <span className="text-lime-300 italic">But are they converting?</span>
        </h3>
        <p className="text-slate-300 text-sm md:text-base leading-relaxed max-w-3xl">
          Traffic is step one. Turning visitors into customers is a design problem, not a mystery.
          Here's what should happen when someone lands on a <span className="text-white font-semibold">{scenarioLabel}</span> site: tailored to the {label.toLowerCase()} category.
        </p>
      </div>

      {/* 5-step walkthrough */}
      <div className="space-y-3 mb-8" data-testid="conversion-steps">
        {steps.map((step, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: -12 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.4, delay: i * 0.06 }}
            className="bg-[#0c465f] rounded-2xl border border-cyan-400/15 hover:border-cyan-400/35 transition-colors p-4 md:p-5"
          >
            <div className="flex items-start gap-4 md:gap-5">
              {/* Step number */}
              <div className="flex-shrink-0 w-10 h-10 md:w-12 md:h-12 rounded-full bg-cyan-500/10 ring-1 ring-cyan-400/30 flex items-center justify-center">
                <span className="text-cyan-300 text-sm md:text-base font-black tabular-nums">0{i + 1}</span>
              </div>
              {/* Title + copy */}
              <div className="flex-1 min-w-0">
                <h4 className="text-white text-base md:text-lg font-bold leading-tight mb-1.5">
                  {step.title}
                </h4>
                <p className="text-slate-300 text-sm md:text-[15px] leading-relaxed">
                  {step.copy}
                </p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Biggest leak + Quick win — 2-column */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5">
        {/* Biggest leak */}
        <div className="bg-rose-500/5 rounded-2xl border border-rose-400/25 p-5 md:p-6" data-testid="conversion-leak">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-rose-300" />
            <p className="text-rose-300 text-[11px] md:text-xs font-bold uppercase tracking-[0.25em]">
              The Biggest Leak
            </p>
          </div>
          <p className="text-white text-sm md:text-base leading-relaxed">
            {biggestLeak}
          </p>
        </div>

        {/* Quick win */}
        <div className="bg-lime-400/5 rounded-2xl border border-lime-400/30 p-5 md:p-6" data-testid="conversion-quickwin">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-lime-300" />
            <p className="text-lime-300 text-[11px] md:text-xs font-bold uppercase tracking-[0.25em]">
              A Quick Win This Week
            </p>
          </div>
          <p className="text-white text-sm md:text-base leading-relaxed">
            {quickWin}
          </p>
        </div>
      </div>

      {/* Meta footer */}
      <div className="mt-6 pt-4 border-t border-cyan-300/10">
        <p className="text-cyan-200/50 text-[11px] leading-relaxed">
          <ArrowRight className="inline w-3 h-3 mr-1 -mt-0.5" />
          This checklist is tailored for <span className="text-cyan-200/80 font-medium">{label}</span>.
        </p>
      </div>
    </div>
  );
};
